import { defineMessages, MessageDescriptor } from "react-intl";
import { Metric } from "@/api/generated/models/metric";
import { RangeMetric } from "@/components/ui/filters/metrics/MetricFilter";

export const ViewMode = {
  trajectories: "trajectories",
  aggregate: "aggregate",
} as const;

export type ViewMode = (typeof ViewMode)[keyof typeof ViewMode];

export type ToggleMetric = Extract<
  Metric,
  "speed" | "acceleration" | "density" | "vehicleType"
>;

export const TOGGLE_METRICS: ToggleMetric[] = [
  Metric.speed,
  Metric.acceleration,
  Metric.density,
  Metric.vehicleType,
];

export const AGGREGATE_METRICS = new Set<Metric>([
  Metric.averageSpeed,
  Metric.averageAcceleration,
  Metric.density,
]);

const METRIC_TO_TILE_MAP: Record<
  ToggleMetric,
  Partial<Record<ViewMode, Metric>>
> = {
  [Metric.speed]: {
    trajectories: Metric.speed,
    aggregate: Metric.averageSpeed,
  },
  [Metric.acceleration]: {
    trajectories: Metric.acceleration,
    aggregate: Metric.averageAcceleration,
  },
  [Metric.density]: { aggregate: Metric.density },
  [Metric.vehicleType]: { trajectories: Metric.vehicleType },
};

export function getMetricTypeForViewMode(
  metric: ToggleMetric,
  viewMode: ViewMode,
): Metric {
  const mapping = METRIC_TO_TILE_MAP[metric];
  const effective = mapping[viewMode];
  if (!effective) {
    const defaultView = getDefaultViewMode(metric);
    return mapping[defaultView] as Metric;
  }
  return effective;
}

export const METRIC_VIEW_AVAILABILITY: Record<
  ToggleMetric,
  { trajectories: boolean; aggregate: boolean }
> = {
  [Metric.speed]: { trajectories: true, aggregate: true },
  [Metric.acceleration]: { trajectories: true, aggregate: true },
  [Metric.density]: { trajectories: false, aggregate: true },
  [Metric.vehicleType]: { trajectories: true, aggregate: false },
};

export function getDefaultViewMode(metric: ToggleMetric): ViewMode {
  const avail = METRIC_VIEW_AVAILABILITY[metric];
  return avail.trajectories ? ViewMode.trajectories : ViewMode.aggregate;
}

const unitMessages = defineMessages({
  speed: {
    id: "metric.unit.speed",
    defaultMessage: "km/h",
  },
  acceleration: {
    id: "metric.unit.acceleration",
    defaultMessage: "m/s\u00B2",
  },
  density: {
    id: "metric.unit.density",
    defaultMessage: "datapts",
  },
});

export const METRIC_CONFIG: Record<
  RangeMetric,
  { unit: MessageDescriptor; step: number }
> = {
  speed: { unit: unitMessages.speed, step: 5 },
  acceleration: { unit: unitMessages.acceleration, step: 5 },
  density: { unit: unitMessages.density, step: 100 },
};
